import { ReactComponent as WarningIndicator } from '@/assets/svgs/warning-indicator.svg'
import Button from '@/components/Button'
import DataElementCard from '@/components/DataElementCard/index'
import DataElementsList from '@/components/DataElementsList'
import TemplateBox from '@/components/TemplateBox/index'
import Toast2 from '@/components/Toast2'
import FloatingPanel from '@/componentsDS/FloatingPanel/FloatingPanel.jsx'
import { FloatingPanelType } from '@/componentsDS/FloatingPanel/FloatingPanel.type.js'
import CardConfigure from '@/componentsDS/Modal/CardConfigure'
import Modal from '@/componentsDS/Modal/Modal'
import Stepper from "@/componentsDS/Stepper/Stepper.jsx"
import { featureConstant } from '@/constants/configuration/feature.js'
import ERROR_MESSAGE_COPIES from "@/constants/error-messages/index"
import fileExtensions from "@/constants/file-extensions/index"
import currentStatuses from '@/constants/statuses/index'
import useBehalfPanel from '@/hooks/use-behalf-panel/index'
import useCDI from '@/hooks/use-cdi'
import useConfig from '@/hooks/use-config'
import { useFeature } from '@/hooks/use-feature/index.js'
import useMessages from '@/hooks/use-messages'
import useModal from '@/hooks/use-modal/index'
import usePage from '@/hooks/use-page'
import useShareDataElement from '@/hooks/use-share-data-element/index'
import useStepList from '@/hooks/use-step-list'
import { useTradex } from "@/hooks/use-tradex/index.js"
import { useLocation, useNavigate } from '@/lib/router-dom/index'
import api from '@/services/api/dataElements.js'
import API_FILES from '@/services/api/files.js'
import "@/translations/i18n"
import { replacePlaceholders } from '@/utils/ajv-error-mapping/helper'
import generateFileTypeErrorMessage from '@/utils/generate-file-type-error-message.js'
import { getEnvAndDex } from "@/utils/get-env-and-dex.js"
import handle from '@/utils/handle'
import rem from '@/utils/rem'
import jschardet from 'jschardet'
import { isEmpty } from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from "react-i18next"
import { useSelector } from 'react-redux'
import {
	AssignUploadingAttachments,
	CombineAttachment,
	ContainAttachments,
	FindAttachments,
	GetAttachmentFiles,
	HandleAttachmentFiles,
	HandleDownloadSingleFileFunction,
	HasDuplicatePath,
	IsPathMismatch,
	ReadCSV,
	ReadJSON,
	UploadAttachments,
	ValidateAttachmentsFunction,
	ValidateDataFunction,
	ValidatePayloadFunction
} from './share-data-functions.jsx'
import useFlowDetection from '@/hooks/use-ap-flow-detection/index.js'
import { CombinedFlows, TransactionTypes } from '@/hooks/use-ap-flow-detection/flow-types.js'
import { addAgentParticipants, buildParticipantList, handleRecipientChangeLogic, shouldClearPrincipalsOnBehalfOfChange, validateParticipantSelection, getAvailableTransactionTypes, filterParticipantsByTransactionType, shouldClearOnBehalfOfOnPrincipalsChange, filterPrincipalsByOnBehalfOf } from '@/hooks/use-ap-flow-detection/participant-list-builder.js'

const SharePage = ({
	defaultFilesData = [],
	defaultAttachmentFiles = [],
	defaultSubmitAPIStatus = false
}) => {

	/*=================================================
		General
	/================================================*/
	const { t } = useTranslation();
	const [pageTitle] = useState(t("shareData"))
	const { goTo } = useNavigate();

	const shareDataElementState = useSelector(state => state.shareDataElement)
	const [dataElementSchema, setDataElementSchema] = useState();
	const [submitAPIStatus, setSubmitAPIStatus] = useState(defaultSubmitAPIStatus);
	const [updatedAttachmentsPayload, setUpdatedAttachmentsPayload] = useState([]);
	const [fileContent, setFileContent] = useState(null);
	const [pathMismatch, setPathMismatch] = useState(false);
	const floatingPanelRef = useRef(null);

	/*=================================================
		Error Modal
	/================================================*/
	const [error, setError] = useState()
	const [errorList, setErrorList] = useState([]);

	usePage({ title: pageTitle})
	const { visible: cancelModal, show: showCancelModal, hide: hideCancelModal } = useModal()
	const { visible: previousModal, show: showPreviouslModal, hide: hidePreviousModal } = useModal()
	const { visible: mismatchModal, show: showMismatchModal, hide: hideMismatchModal } = useModal()
	const { visible: duplicateModal, show: showDuplicateModal, hide: hideDuplicateModal } = useModal()

	/*=================================================
		Share Data Element Hook
	/================================================*/
	const {
		handleDataElement,
		handleRecipient,
		handleOnBehalfOf,
		handleStoreFor,
		handleReferenceID,
		handleAction,
		handlePayload,
		handleAttachment,
		handleRequiredAttachments,
		handleStatus,
		handleParticipantList,
		handlestoreForList,
		handleOnBehalfOfList,
		handleUpdateState,
		handleMasterData,
		handleReset,
		handlePrincipalsList,
		handleSelectedPrincipals,
		handleTransactionType
	} = useShareDataElement()

	const principalsList = shareDataElementState?.principalsList || [];
	const selectedPrincipals = shareDataElementState?.selectedPrincipals || [];
	const transactionType = shareDataElementState?.transactionType || TransactionTypes.PUSH_STORE;

	/*=================================================
		Dropzone
	/================================================*/
	const [files, setFiles] = useState(defaultFilesData)
	const [attachmentFiles, setAttachmentFiles] = useState(defaultAttachmentFiles)

	const { dex } = getEnvAndDex();
	const isTradex = useTradex();
	const isErrorMessageEnhancement = useFeature(featureConstant.ERROR_MESSAGE_ENHANCEMENT);
	const isAgentPrincipalUiEnabled = useFeature(featureConstant.AGENT_PRINCIPAL_UI);
	const isMasterDataValidationEnabled = useFeature(featureConstant.MASTER_DATA_VALIDATION);

	// Get apiSpecUrl from Redux store
	const { apiSpecUrl } = useSelector(state => state.auth || {})
	const onDropRejectedPayload = useCallback(_rejectedFiles => {

		if (isErrorMessageEnhancement) {
			// Use the common error header message
			setError(ERROR_HEADER_MESSAGE);

			// Create error list items
			const errorItems = [];

			// Check if rejection is due to file size or file type
			if (_rejectedFiles && _rejectedFiles.length > 0) {
				_rejectedFiles.forEach(rejectedFile => {
					if (rejectedFile.errors) {
						// Check for file size error
						const sizeError = rejectedFile.errors.find(err => err.code === 'file-too-large');

						if (sizeError) {
							errorItems.push({
								message: replacePlaceholders(ERROR_MESSAGE_COPIES.payload.size, MAX_PAYLOAD_FILE_SIZE_IN_MB)
							});
						} else {
							// This is a file type error
							errorItems.push({
								message: ERROR_MESSAGE_COPIES.payload.fileType
							});
						}
					}
				});
			}

			// Set the error list and clear any files
			return setErrorList(errorItems);
		} else {
			return setError(generateFileTypeErrorMessage(['.json', '.csv']));
		}
	}, []);

	const onDropPayload = useCallback(acceptedFile => {
		acceptedFile?.forEach(file => {
            // First check encoding using array buffer
            const binaryReader = new FileReader()
            binaryReader.onload = () => {
                // Convert ArrayBuffer to string for encoding detection
                const buffer = binaryReader.result;
                const bytes = new Uint8Array(buffer);
                const binaryString = bytes.reduce((str, byte) => str + String.fromCharCode(byte), '');

                const fileEncoding = jschardet.detect(binaryString).fileEncoding;
                if (fileEncoding && fileEncoding.toLowerCase() !== 'utf-8' && fileEncoding.toLowerCase() !== 'ascii') {
                    setFiles([])
                    setError("Error: Invalid file type, please convert to UTF-8 encoding.")
                    return
                }

                // Then read as text for validation
                const textReader = new FileReader();
                textReader.onload = () => {
                    try {
                        setFileContent(textReader.result);
                        setFiles([])
                        setFiles(files => [...files, Object.assign(file, {
                            preview: URL.createObjectURL(file)
                        })]);
                    } catch (error) {
                        setFiles([]);
                        setFileContent(null);
                        setError("Error: Invalid JSON file format. Please check the file content.");
                    }
                };
                textReader.readAsText(file);
            }
            binaryReader.readAsArrayBuffer(file)
        })
    }, [])

	const onDropAttachment = useCallback(acceptedFiles => {
		const newFile = acceptedFiles.map(file => Object.assign(file, {
			preview: URL.createObjectURL(file)
		}))

		if (newFile.length === 0) {
			setError(fileExtensions.errorMessage)
			return
		}

		setAttachmentFiles(oldFiles => {
			const checkedOldFile = oldFiles.filter(oldFile => !newFile.find(newFile => newFile.name === oldFile.name))
			const sumAllFiles = checkedOldFile.reduce((prev, next) => { return prev + next.size }, 0);

			if ((sumAllFiles + newFile[0].size) > MAX_ATTACHMENTS_TOTAL_SIZE_IN_MB * 1000000) {

				if (isErrorMessageEnhancement) {
					// Use the common error header message
					setError(ERROR_HEADER_MESSAGE);

					// Create error list for the floating error button
					const errorListItems = [
						{
							message: replacePlaceholders(ERROR_MESSAGE_COPIES.attachment.size, MAX_ATTACHMENTS_TOTAL_SIZE_IN_MB),
						}
					];
					setErrorList(errorListItems);
				}
				else {
					setError("Total files upload is more tha 250mb")
				}
				return checkedOldFile
			}
			const allAttachedFiles = checkedOldFile.concat(newFile);
			return allAttachedFiles
		})

	}, [])

	// Define max file size for payload files (250MB, same as attachments)
	const MAX_PAYLOAD_FILE_SIZE_IN_MB = 250; // 250MB in bytes
	const MAX_ATTACHMENTS_TOTAL_SIZE_IN_MB = 250; // 250MB in bytes

	const { getRootProps: getRootPropsPayload, getInputProps: getInputPropsPayload } = useDropzone({
		onDrop: onDropPayload,
		onDropRejected: onDropRejectedPayload,
		multiple: false,
		accept: 'application/json, text/csv',
		maxSize: MAX_PAYLOAD_FILE_SIZE_IN_MB * 1000000 // Add size validation in MB
	})
	const { getRootProps: getRootPropsAttachment, getInputProps: getInputPropsAttachment } = useDropzone({ onDrop: onDropAttachment, multiple: true, accept: fileExtensions.type })

	useEffect(() => {
		if (files.length === 0) {
			return
		}
		callbackA(files, shareDataElementState?.dataElement)
		handleAction({ type: "UPLOAD", value: files[0]?.path })
	}, [files])

	useEffect(() => {
		if (files.length === 0) {
			return
		}
	}, [files])

	useEffect(() => {
		if (attachmentFiles.length > 0) {
			const isPathMismatch = IsPathMismatch(attachmentFiles, shareDataElementState?.requiredAttachments);
			setPathMismatch(isPathMismatch);
			const checkDuplicatePath = HasDuplicatePath(attachmentFiles, shareDataElementState?.attachments);
			const handleAttachmentFiles = HandleAttachmentFiles(attachmentFiles, shareDataElementState?.attachments);

			if (shareDataElementState.action.type !== "EFORM" && isPathMismatch) {

				if (isErrorMessageEnhancement) {
					setError(ERROR_HEADER_MESSAGE);
					const errorListItems = [
						{
							message: ERROR_MESSAGE_COPIES.attachment.pathMismatch,
							type: 'ERROR'
						}
					];
					setErrorList(errorListItems);
					setAttachmentFiles([])
					return;
				} else {
					return showMismatchModal();
				}
			}  else {
				setError('');
				setErrorList([]);
				setPathMismatch(false);
			}

			if (checkDuplicatePath) {
				clearAttachment();
				return showDuplicateModal();
			}

			clearAttachment();
			handleAttachment(handleAttachmentFiles);
		}
	}, [attachmentFiles])

	useEffect(() => {
		if (error && isErrorMessageEnhancement && floatingPanelRef.current) {
			floatingPanelRef.current.open();
		}
	}, [error, errorList, isErrorMessageEnhancement]);
	// Common error header message for FloatingErrorButton
	const ERROR_HEADER_MESSAGE = "Please correct the errors below and upload the file again:";

	function handleError() {
		setError()
	}

	/*=================================================
		API
	/================================================*/
	const { identity, provideItems, error: errorConfig } = useConfig()
	const [behalfPanel] = useBehalfPanel(shareDataElementState?.onBehalfOfList)
	const { create: createMessages } = useMessages()
	const { push, } = useCDI()

	const flowInfo = useFlowDetection(
		shareDataElementState?.dataElement,
		{
		recipient: shareDataElementState?.recipient,
		onBehalfOf: shareDataElementState?.onBehalfOf,
		myPrincipals: shareDataElementState?.selectedPrincipals,
		},
		identity,
		transactionType
	)

	const availableTransactionTypes = isAgentPrincipalUiEnabled ? getAvailableTransactionTypes(flowInfo?.availableRoles) : { hasPushStore: true, hasPushToAgent: false };

	useEffect(() => {
		if (!isAgentPrincipalUiEnabled || !flowInfo?.availableRoles) return;
		
		if (!availableTransactionTypes.hasPushStore && availableTransactionTypes.hasPushToAgent) {
			handleTransactionType('PUSH_TO_AGENT');
		}
	}, [flowInfo?.availableRoles?.hasContributorToAgent, flowInfo?.availableRoles?.hasContributorToConsumer, flowInfo?.availableRoles?.hasPOB, flowInfo?.availableRoles?.hasPOBOP, isAgentPrincipalUiEnabled]);

	useEffect(() => {
		if(!isAgentPrincipalUiEnabled){
			const participantList = shareDataElementState?.dataElement?.to
				?.map(element => element)
				.reduce(
					(array, participant) => {
						if (!(participant.hasOwnProperty('for_principals'))) {
							return [...array, participant];
						}
						return array;
					},[
					{
						id: "MY_PITSTOP",
						name: t("myPitstop"),
						iconUrl: identity?.organization.icon_url,
						on_behalf_of: []
					}
				]
				)
			handleParticipantList(participantList)

			handlestoreForList(participantList?.filter((e) => e.id !== "MY_PITSTOP"))
		}
	}, [shareDataElementState?.dataElement])
	

	useEffect(() => {
		if(isAgentPrincipalUiEnabled){
			const baseParticipantList = buildParticipantList(shareDataElementState?.dataElement, identity, t);
			
			const withAgents = addAgentParticipants(baseParticipantList, shareDataElementState?.dataElement);
			
			const filteredList = filterParticipantsByTransactionType(withAgents, transactionType);
			
			handleParticipantList(filteredList);
			handlestoreForList(filteredList?.filter((e) => e.id !== "MY_PITSTOP"));
		}
	}, [shareDataElementState?.dataElement, flowInfo?.combinedFlow, identity, transactionType]);

	useEffect(() => {
		if (!isAgentPrincipalUiEnabled) return;
		
		handleRecipient([]);
		handleOnBehalfOf([]);
		handleSelectedPrincipals([]);
	}, [transactionType]);

	useEffect(() => {
		if (shareDataElementState?.recipient?.find(item => item.id === 'MY_PITSTOP')) {
			handleOnBehalfOfList(shareDataElementState?.participantsList.reduce((pre, cur) => [...pre, ...cur.on_behalf_of], []).filter((val, idx, arr) => idx === arr.findIndex(v => v.id === val.id)))
		} else {
			handleOnBehalfOfList(shareDataElementState?.recipient?.reduce((pre, cur) => [...pre, ...cur.on_behalf_of], []))
		}
	}, [shareDataElementState?.recipient, shareDataElementState?.participantsList])

	useEffect(async () => {
		if (submitAPIStatus) {
			const myPitstop = shareDataElementState?.recipient?.find(participant => participant.id === 'MY_PITSTOP')
			const othersPitstops = shareDataElementState?.recipient?.filter(participant => participant.id !== 'MY_PITSTOP')

			const allPushPromises = [];

			if (myPitstop) {
				allPushPromises.push(storeMessage(updatedAttachmentsPayload))
			}

			if (othersPitstops.length > 0) {
				allPushPromises.push(pushMessage(updatedAttachmentsPayload, othersPitstops))
			}

			try {
				const allSettled = await Promise.allSettled(allPushPromises)
				const allErrors = allSettled.filter(result => result.status === 'rejected')
				const allSuccess = allSettled.filter(result => result.status === 'fulfilled')

				if (allErrors.length > 0) {
					throw setError(`Unable to push, wrong data file for ${shareDataElementState?.dataElement?.name} was uploaded. Please check and try again.`)
				}

				if (allSuccess.length === allSettled.length) {
					handleReset();
					goTo('/shared-data/share/success')
				}
			}
			catch (error) {
				setError(error?.error?.message || error?.message || "Unable to submit push request")
			}
			finally {
				setSubmitLoading(false)
			}
		}
	}, [submitAPIStatus])

	useEffect(() => {
		setError(errorConfig)
	}, [errorConfig])

	/*=================================================
		Steps
	/================================================*/
	const stepArr= [
		{stepTitle : 'Choose Data Element'},
		{stepTitle : 'Fill Data Elements'}
	]
	const [steps] = useState([...stepArr])
	const [stepSelected, setStepSelected] = useState([])
	const { step, goToStep, prevStep, nextStep } = useStepList(steps)

	function handleNext() {
		nextStep()
		setStepSelected((currentArray) => {
			currentArray[0] = shareDataElementState?.dataElement?.name
			return currentArray
		})
	}

	function handlePrevious() {
		if (shareDataElementState?.status === currentStatuses.in_progress || shareDataElementState?.status === currentStatuses.complete) {
			return showPreviouslModal()
		}
		prevStep()
	}

	function resetState() {
		handleReset()
		prevStep()
	}

	/*=================================================
		Location
	/================================================*/
	const  { currentState } = useLocation();

	useEffect(() => {
		if (isEmpty(currentState)) {
			return
		}

		if (!currentState || currentState.type === "new") {
			handleReset()
			return;
		}

		handleUpdateState(currentState)
		setStepSelected((currentArray) => {
			currentArray[0] = currentState?.dataElement?.name
			return currentArray
		})
		goToStep(2)
	}, [currentState]);

	/*=================================================
		E-Form
	/================================================*/
	function onFillEformBtnClick() {
		goTo("/shared-data/eform", { shareDataElementState, dataElementSchema });
	}

	/*=================================================
		Status Change
	/================================================*/
	useEffect(() => {
		if (shareDataElementState?.status === currentStatuses.complete) {
			return handleStatus(currentStatuses.complete)
		}

		if (shareDataElementState?.referenceId === "" && shareDataElementState?.recipient?.length === 0) {
			return handleStatus(currentStatuses.not_started)
		}

		if (shareDataElementState?.referenceId !== "" || shareDataElementState?.recipient?.length > 0) {
			return handleStatus(currentStatuses.in_progress)
		}
	}, [shareDataElementState?.referenceId, shareDataElementState?.recipient, shareDataElementState?.status])

	function areAttachmentsValid(requiredAttachments, attachments, schema) {

		if (pathMismatch)
			 return false;

		// For EFORM actions
		if (shareDataElementState?.action?.type === 'EFORM') {
			return schema?.required?.includes('attachments') ? attachments.length > 0 : true;
		}
		// For UPLOAD actions
		if (shareDataElementState?.action?.type === 'UPLOAD') {
			// If no attachments are required
			if (!schema?.required?.includes('attachments') && requiredAttachments.length === 0) {
				return true;
			}
			
			// If attachments are required, check that all required attachments are present
			if (requiredAttachments.length > 0) {
				// Must have exact number of required attachments
				if (attachments.length !== requiredAttachments.length) {
					return false;
				}
				
				// All attachment paths must match required attachment paths
				return requiredAttachments.every(requiredPath => 
					attachments.some(attachment => attachment.path === requiredPath)
				);
			}
		}
		return false;
	}

	function areRecipientsValid(recipients, storeFor) {
		return recipients.length > 0 &&
			((recipients[0].id === "MY_PITSTOP" && ((storeFor !== null && storeFor.length > 0) || storeFor === null)) ||
				(recipients[0].id !== "MY_PITSTOP"));
	}

	useEffect(() => {
		const payload = shareDataElementState?.payload || []
		const onBehalf = shareDataElementState?.onBehalfOf || []
		const selectedPrincipalsState = shareDataElementState?.selectedPrincipals || []
		const requiredAttachments = shareDataElementState?.requiredAttachments || []
		const attachments = shareDataElementState?.attachments || []
		const recipients = shareDataElementState?.recipient || []
		const storeFor = shareDataElementState?.storeFor || null
		const schema = shareDataElementState?.dataElement?.schema || {}

		const isValidAttachments = areAttachmentsValid(requiredAttachments, attachments, schema);
		const isValidRecipients = areRecipientsValid(recipients, storeFor);
		
		const isValidSelection = validateParticipantSelection(isAgentPrincipalUiEnabled, recipients, onBehalf, selectedPrincipalsState, behalfPanel, transactionType);

		if (payload.length > 0 && isValidSelection && 
			isValidAttachments && isValidRecipients) {
			return handleStatus(currentStatuses.complete)
		} else {
			handleStatus(currentStatuses.in_progress)
		}
	}, [
		shareDataElementState?.payload,
		behalfPanel,
		shareDataElementState?.onBehalfOf,
		shareDataElementState?.selectedPrincipals,
		shareDataElementState?.requiredAttachments,
		shareDataElementState?.attachments,
		shareDataElementState?.recipient,
		shareDataElementState?.storeFor,
		shareDataElementState?.dataElement,
		identity,
		transactionType
	])


	/*=================================================
		JSON & CSV Template
	/================================================*/
	const [csvPath, setCsvPath] = useState(null)
	const [jsonPath, setJsonPath] = useState(null)

	useEffect(() => {

		if (shareDataElementState?.dataElement?.id) {
			getTemplate(shareDataElementState?.dataElement.id)
		}

		async function getTemplate(id) {

			const [error, response] = await api.getDataElementTemplate(id)

			if (error) return setError(error?.error?.message || "Unable to retrieve JSON template")

			const jsonPath = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(response.jsonTemplate))}`
			const csvPath = `data:text/csv;charset=utf-8,${encodeURIComponent(response.csvTemplate?.replace(/\n/g, '\r\n'))}` // Ensure CRLF line endings


			setDataElementSchema(response.elementSchema)
			setJsonPath(jsonPath)
			setCsvPath(csvPath)

			if (isMasterDataValidationEnabled) {
				if (response.masterData) {
					handleMasterData(response.masterData)
				} else {
					handleMasterData(null)
				}
			}
		}

	}, [shareDataElementState?.dataElement])

	function clearAttachment() {
		setAttachmentFiles([]);
	}
	async function callbackA(files, dataElement) {
		if (files?.length > 0) {
			const file = files[0];
			let json;
			try {
				if (file.type === "application/json") {
					json = await ReadJSON(file);
				} else if (file.type === "text/csv" || file.type === "application/vnd.ms-excel") {
					json = await ReadCSV(file, dataElement.schema, isErrorMessageEnhancement);
				} else {
					handleRemoveFile();
					return;
				}
			} catch (error) {

				if (isErrorMessageEnhancement) {
					// Handle error from ReadJSON or ReadCSV
					// Use the common error header message
					setError(ERROR_HEADER_MESSAGE);

					// Create detailed error list for FloatingErrorButton
					const errorItems = [{
						message: <div dangerouslySetInnerHTML={{ __html: replacePlaceholders(ERROR_MESSAGE_COPIES.payload.structure, apiSpecUrl) }} />,
					}];
					setErrorList(errorItems);

				}

				handleRemoveFile();
				return;
			}

			const masterData = isMasterDataValidationEnabled ? shareDataElementState?.masterData : null;
			
			if (isErrorMessageEnhancement) {
				// Pass fileContent to ValidateDataFunction for accurate line numbers
				const payloadValidation = ValidatePayloadFunction(json, dataElement.schema, file.type === "text/csv", fileContent, apiSpecUrl, false, masterData);
				const isAttachmentsValid = ValidateAttachmentsFunction(json);
				if (!payloadValidation.isValid || isAttachmentsValid) {
					if (payloadValidation.structureErrorMessage) {
						setError(ERROR_HEADER_MESSAGE);

						// Create detailed error list for FloatingErrorButton
						const errorItems = [{
							message: <div dangerouslySetInnerHTML={{ __html: payloadValidation.structureErrorMessage }} />,
						}];
						setErrorList(errorItems);
					} else {
						// Format errors to display like in the UI
						const formattedErrors = [];

						// Process all error messages from validation result
						if (payloadValidation.errors && payloadValidation.errors.length > 0) {
							// Flatten all errors from all payloads
							const allErrors = payloadValidation.errors.reduce((acc, item) => {
								if (item.errors) {
									Object.entries(item.errors).forEach(([path, messages]) => {
										if (!acc[path]) {
											acc[path] = [];
										}
										// Deduplicate messages for each path
										messages.forEach(msg => {
											if (!acc[path].includes(msg)) {
												acc[path].push(msg);
											}
										});
									});
								}
								return acc;
							}, {});

							// Convert to array of error messages and make field names bold
							Object.values(allErrors).forEach(messages => {
								const processedMessages = messages.map(message => {
									const parts = message.split(':');
									if (parts.length >= 2) {
										const fieldPart = parts[0];
										const errorPart = parts.slice(1).join(':');
										return { message: <><b>{fieldPart}</b>:{errorPart}</> };
									}
									return { message };
								});
								formattedErrors.push(...processedMessages);
							});
						}

						// Use the common error header message
						setError(ERROR_HEADER_MESSAGE);

						setErrorList(formattedErrors);
					}
					handleRemoveFile();
					return;
				}
				// TO DO: handle attachment validation

			} else {
				if (!ValidateDataFunction(json, dataElement.schema) || ValidateAttachmentsFunction(json)) {
					if (dataElement?.name) {
						setError(`Invalid data uploaded for ${dataElement.name}. Please upload the correct .CSV or .JSON file.`);
					} else {
						setError("Unable to submit pull request")
					}
					// TO DO: handle attachment validation
					handleRemoveFile();
					return;
				}
			}

			setError('')
			setErrorList([])
			const attachmentFiles = GetAttachmentFiles(json, dex);

			handlePayload(json);
			handleAttachment([]);
			handleRequiredAttachments(attachmentFiles);
		} else {
			handleRemoveFile();
		}
	}

	/*=================================================
		Submit
	/================================================*/
	const [submitLoading, setSubmitLoading] = useState()


	// For own Pitstop
	async function storeMessage(jsonData) {

		const payload = {
			dataElementId: shareDataElementState?.dataElement.id,
			payload: jsonData,
			orgId: shareDataElementState?.onBehalfOf ? shareDataElementState?.onBehalfOf.id : null,
			on_behalf_of: shareDataElementState?.onBehalfOf ? shareDataElementState?.onBehalfOf : [],
			referenceId: shareDataElementState?.referenceId,
			storedFor: shareDataElementState?.storeFor
		}

		const [error, data] = await handle(createMessages(payload))
		if (error) {
			throw setError(error)
		}
		return data
	}

	// For others Pitstops
	async function pushMessage(jsonData, othersPitstops) {

		if (!isAgentPrincipalUiEnabled) {
			const payload = {
				participants: othersPitstops.map(participant => ({ ...participant, meta: { data_ref_id: shareDataElementState?.referenceId } })),
				payload: jsonData,
				on_behalf_of: shareDataElementState?.onBehalfOf ? shareDataElementState?.onBehalfOf : [],
			}

			payload.participants = payload.participants.map(({ on_behalf_of, ...rest }) => rest)

			const [error, data] = await handle(push(shareDataElementState?.dataElement.id, payload))
			if (error) {
				throw error
			}
			return data
		}
		
		const payload = {
			...(flowInfo?.activeFlow === CombinedFlows.POB_ONLY && {
				on_behalf_of: shareDataElementState?.onBehalfOf ? shareDataElementState?.onBehalfOf : [],
			}),
			...(flowInfo?.activeFlow === CombinedFlows.POBOP_ONLY && transactionType === TransactionTypes.PUSH_STORE && {
				for_principals: selectedPrincipals.map(
					(principal) => principal.uen
				),
			}),
			participants: othersPitstops.map((participant) => {
				const cleanParticipant = {
					name: participant.name,
					id: participant.isAgent || participant.isPOBOP
						? participant.originalId
						: participant.id,
					iconUrl: participant.iconUrl || "",
					systems: participant.systems,
					meta: { data_ref_id: shareDataElementState?.referenceId },
				}

				if (participant.isAgent && selectedPrincipals.length > 0) {
					cleanParticipant.principals = selectedPrincipals.map((principal) => principal.uen);
				}

				return cleanParticipant
			}),
			payload: jsonData,
		}
		payload.participants = payload.participants.map(({ on_behalf_of, ...rest }) => rest)

		const [error, data] = await handle(
			push(shareDataElementState?.dataElement.id, payload)
		)
		if (error) throw error
		return data
	}

	async function submitPayload(jsonData) {
		if (shareDataElementState?.attachments && shareDataElementState?.attachments.length > 0) {

			if (!isTradex) {
				//Process DE and Field Level Attachments
				jsonData = await Promise.all(jsonData.map(async (jsonObj, index) => {
					const [errors, resultJSON] = await UploadAttachments(jsonObj);
					if (errors && errors.length) {
						setSubmitLoading(false)
						return setError(errors[0]?.error?.message || error?.message || "Unable to submit push request");
					}
					return resultJSON;
				}));
			}
			else {
				const promises = jsonData.map(async (item, index) => {
					const [error, uploadFilesResult] = await API_FILES.upload(item.attachments);

					if (error) {
						setSubmitLoading(false)

						return setError(error?.error?.message || error?.message || "Unable to submit push request");
					}

					jsonData[index].attachments = uploadFilesResult?.reduce((pre, cur) => [...pre, ...cur.data.data], []);
				})

				await Promise.all(promises);
			}
			if (!submitLoading) {
				setUpdatedAttachmentsPayload(jsonData)
				return setSubmitAPIStatus(true)
			}
		}
		else {
			if (!isTradex) {
				//Process DE and Field Level Attachments
				jsonData = await Promise.all(jsonData.map(async (jsonObj, index) => {
					const [errors, resultJSON] = await UploadAttachments(jsonObj);
					if (errors && errors.length) {
						setSubmitLoading(false);
						return setError(errors[0]?.error?.message || errors[0]?.message || "Unable to submit push request");
					}
					return resultJSON;
				}));
			}
			if (!submitLoading) {
				setUpdatedAttachmentsPayload(jsonData)
				return setSubmitAPIStatus(true)
			}
		}
	}

	async function submit() {

		if (submitLoading) {
			return
		}

		setSubmitLoading(true)

		if (!isTradex) {
			const jsonData = shareDataElementState?.payload.map(data => {

				if (ContainAttachments(shareDataElementState?.dataElement) && shareDataElementState?.attachments?.length) {
					if (shareDataElementState.action.type === "EFORM") {
						return { ...data, attachments: shareDataElementState?.attachments }
					}
					else {
						return AssignUploadingAttachments(shareDataElementState?.attachments, data);
					}
				}
				else {
					return { ...data, attachments: undefined };
				}
			})

			await submitPayload(jsonData);
		}
		else {
			const jsonData = shareDataElementState?.payload.map(data => {
				return {
					...data,
					attachments: (ContainAttachments(shareDataElementState?.dataElement) && shareDataElementState?.attachments?.length) ?
						(shareDataElementState.action.type === "EFORM" ? (shareDataElementState?.attachments) : shareDataElementState?.attachments.filter((item) => data.attachments?.some((val) => val.filename === item.name)))
						: undefined
				}
			})

			await submitPayload(jsonData);
		}
	}

	/*=================================================
		Handle File
	/================================================*/
	function handleRemoveFile() {
		handlePayload([])
		handleAttachment([])
		handleRequiredAttachments([])
		handleAction({ type: "", value: "" })
		setFileContent(null) // Clear file content when removing file
	}

	function handleRemoveSingleFile(item) {
		const newFile = shareDataElementState?.attachments.filter((attachment) => attachment.path !== item)
		handleAttachment(newFile)
	}

	function handleDownloadSingleFile(item) {
		HandleDownloadSingleFileFunction(item, shareDataElementState)
	}

	/*=================================================
		Additional Function
	/================================================*/
	function handleRecipientChange(option) {
		if (isAgentPrincipalUiEnabled && option?.length > 0) {
			const isValidRecipient = shareDataElementState?.participantsList?.some(
				(participant) => participant.id === option[0]?.id
			);
			
			if (!isValidRecipient) { // Ignore stale selection that's not in current participant list
				return;
			}
		}

		if (shareDataElementState?.recipient !== option && option.length > 0) {
			handleRecipient(option)
			handleOnBehalfOf([])

			if (option[0].id === "MY_PITSTOP") {
				handleStoreFor([])
			}

			if (option[0].id !== shareDataElementState?.recipient[0]?.id) {
				handleStoreFor(null)
			}
		}

		if(isAgentPrincipalUiEnabled) {
			const { principalsList, selectedPrincipals } = handleRecipientChangeLogic(isAgentPrincipalUiEnabled, option, shareDataElementState?.recipient);

			handlePrincipalsList(principalsList);
			
			if (selectedPrincipals !== undefined) {
				handleSelectedPrincipals(selectedPrincipals);
			}
		}
	}

	const handlePrincipalsChange = (principals) => {
		handleSelectedPrincipals(principals);

		if (shouldClearOnBehalfOfOnPrincipalsChange(
			isAgentPrincipalUiEnabled, 
			shareDataElementState?.recipient, 
			principals,
			transactionType
		)) {
			handleOnBehalfOf([]);
		}

		if (!isAgentPrincipalUiEnabled || !shareDataElementState?.recipient?.[0]?.mergedType) {
			if (shareDataElementState?.recipient?.[0]?.isMerged && principals.length > 0) {
				handleOnBehalfOf([]);
			}
		}
	};

	const handleResetAll = () => {
		// Reset transactionType based on available options
		if (!availableTransactionTypes.hasPushStore && availableTransactionTypes.hasPushToAgent) {
			handleTransactionType(TransactionTypes.PUSH_TO_AGENT);
		} else if (availableTransactionTypes.hasPushStore) {
			handleTransactionType(TransactionTypes.PUSH_STORE);
		}

		// Reset shareDataElementState
		handleRecipient([]);
		handleOnBehalfOf([]);
		handleSelectedPrincipals([]);
		handleStoreFor(null);
		handlePayload([]);
		handleAttachment([]);
		handleRequiredAttachments([]);
		handleAction({ type: "", value: "" });
		handleReferenceID("");
		handleStatus(currentStatuses.not_started);

		// Reset local component states
		setFiles(defaultFilesData);
		setAttachmentFiles(defaultAttachmentFiles);
		setSubmitAPIStatus(defaultSubmitAPIStatus);
		setUpdatedAttachmentsPayload([]);
		setFileContent(null);
		setPathMismatch(false);
		setError(undefined);
		setErrorList([]);
	}

	useEffect(() => {
		if (isAgentPrincipalUiEnabled && shareDataElementState?.recipient?.[0]?.isMerged) {
			return
		}

		if (shareDataElementState?.onBehalfOfList?.length === 1) {
			handleOnBehalfOf(shareDataElementState?.onBehalfOfList)
		}
	}, [shareDataElementState?.onBehalfOfList, shareDataElementState?.recipient])

	function handleStoreForChange(option) {
		if (option === null) {
			return handleStoreFor(null)
		}

		if (shareDataElementState?.storeFor !== option && option.length > 0) {
			return handleStoreFor(option)
		}
		handleStoreFor([])
	}

	function handleOnBehalfOfChange(option) {
		if (shareDataElementState?.onBehalfOf !== option && option.length > 0) {
			handleOnBehalfOf(option);

			if (isAgentPrincipalUiEnabled) {
				if (shareDataElementState?.recipient?.[0]?.isAgent) {
					const { filteredPrincipals } = filterPrincipalsByOnBehalfOf(shareDataElementState.recipient[0], option[0]);

					handlePrincipalsList(filteredPrincipals);
					handleSelectedPrincipals([]);
					return;
				}

				const shouldClear = shouldClearPrincipalsOnBehalfOfChange(
					isAgentPrincipalUiEnabled, 
					shareDataElementState?.recipient, 
					option, 
					identity,
					transactionType
				);

				if (shouldClear) {
					handleSelectedPrincipals([]);
				}
			}
		}
	}

	const { matchingAttachments = [], nonMatchingAttachments = [] } = FindAttachments(shareDataElementState?.attachments, shareDataElementState?.requiredAttachments);
	const { combineAttachments = [], optionalAttachments = [] } = CombineAttachment(shareDataElementState)
	
	return (
		<>
			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">
						{pageTitle}
					</div>
				</div>
				<div className="title-header__right"></div>
			</div>

			<div className="layout-default__full-width--padded">
				<Stepper steps={steps} currentStep={step-1}/>
			</div>

			<hr />

			{step === 1 && (
				<>
					<span className="t--share-data-subheader">Choose a data element to share:</span>
					<div className="data-element-selectors">
						<div className="data-element-selectors__cols">
							<DataElementsList items={provideItems} selected={shareDataElementState?.dataElement} onChange={(e) => handleDataElement(e)} />
						</div>
					</div>
				</>
			)}
			
			{step === 2 && (
				<>
					<span className="t--share-data-subheader">Fill in the selected Data Element:</span>

					<DataElementCard
						//State
						status={shareDataElementState?.status}
						recipient={shareDataElementState?.recipient}
						referenceId={shareDataElementState?.referenceId}
						dataElement={shareDataElementState?.dataElement}
						storeFor={shareDataElementState?.storeFor}
						payload={shareDataElementState?.payload}
						action={shareDataElementState?.action}
						onBehalfOf={shareDataElementState?.onBehalfOf}
						requiredAttachments={shareDataElementState?.requiredAttachments}
						attachments={shareDataElementState?.attachments}

						participantsList={shareDataElementState?.participantsList}
						principals={selectedPrincipals}
						principalsList={principalsList}
						handlePrincipalsChange={handlePrincipalsChange}
						storeForList={shareDataElementState?.storeForList}
						onBehalfOfList={shareDataElementState?.onBehalfOfList}

						//State Value
						combineAttachments={combineAttachments}
						optionalAttachments={optionalAttachments}

						//Function
						handleRecipientChange={handleRecipientChange}
						handleStoreForChange={handleStoreForChange}
						handleOnBehalfOfChange={handleOnBehalfOfChange}
						handleRemoveFile={handleRemoveFile}
						onFillEformBtnClick={onFillEformBtnClick}
						behalfPanel={behalfPanel}
						containAttachments={ContainAttachments(shareDataElementState?.dataElement)}
						matchingAttachments={matchingAttachments}
						nonMatchingAttachments={nonMatchingAttachments}
						handleRemoveSingleFile={handleRemoveSingleFile}
						handleDownloadSingleFile={handleDownloadSingleFile}

						//Dropzone
						getRootPropsPayload={getRootPropsPayload}
						getInputPropsPayload={getInputPropsPayload}
						getRootPropsAttachment={getRootPropsAttachment}
						getInputPropsAttachment={getInputPropsAttachment}

						//Handle Function
						handleDataElement={handleDataElement}
						handleRecipient={handleRecipient}
						handleOnBehalfOf={handleOnBehalfOf}
						handleStoreFor={handleStoreFor}
						handleReferenceID={handleReferenceID}
						handleAction={handleAction}
						handlePayload={handlePayload}
						handleAttachment={handleAttachment}
						handleRequiredAttachments={handleRequiredAttachments}
						handleStatus={handleStatus}
						handleUpdateState={handleUpdateState}
						handleReset={handleReset}
						identity={identity}
						transactionType={transactionType}
						availableTransactionTypes={availableTransactionTypes}
						onTransactionTypeChange={handleTransactionType}
						onReset={handleResetAll}
					/>
				</>
			)}

			{shareDataElementState?.dataElement && (
				<TemplateBox selectedDataElement={shareDataElementState?.dataElement} jsonPath={jsonPath} csvPath={csvPath} />
			)}

			<div className="DS__layout__footer">
				<div className="layout-default__sticky-actions">
					<div className="layout-default__sticky-actions__left">
						{step > 1 && <Button text={t("Cancel")}
							sizing='Medium'
							full noborder maxWidth='154'
							submit={() => showCancelModal()}
							data-testid="cancel-button"
						/>}
					</div>
					<div className="layout-default__sticky-actions__right" style={{ display: 'flex' }}>
						<div className="overview__button_wrap">
							{step > 1 &&
								<Button text={t("previous")}
									sizing='Medium' ghost full maxWidth='154'
									submit={() => handlePrevious()}
									data-testid="previous-button"
								/>
							}
							{(step > 0 && step !== steps.length) &&
								<Button text={t("next")}
									sizing='Medium' full maxWidth='154'
									disabled={isEmpty(shareDataElementState?.dataElement)}
									submit={() => handleNext()}
									data-testid="next-button"
								/>
							}
							{step === steps.length &&
								<Button
									text={t("submit")}
									sizing='Medium' full maxWidth='154'
									disabled={shareDataElementState?.status !== currentStatuses.complete}
									submit={() =>
										submit().catch((error) => {
											setSubmitLoading(false)
											setError(error)
										})
									}
									loading={submitLoading}
									data-testid="submit-button"
									id="share-data-submit-button-id"
								/>
							}
						</div>
					</div>
				</div>
			</div>

			{
				submitLoading && (
					ReactDOM.createPortal(
						<div className='modal white'>
							<div className='loading'>
								<img src="/loading.gif" />
							</div>
						</div>
						, document.body
					)
				)
			}
			{error && isErrorMessageEnhancement ? (
				<FloatingPanel
					title={`${errorList.length} errors found`}
					messageHeader={error}
					messages={errorList}
					type={FloatingPanelType.DESTRUCTIVE}
					className='eform-floating-panel'
					ref={floatingPanelRef}
				/>
			) : (
				<Toast2
					show={error}
					errorMessage={error}
					title={"Invalid File"}
					warningIcon={true}
					close={() => { handleError() }}
				/>
			)}

			<Modal visible={cancelModal} variant="medium" close={hideCancelModal}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__left">
										<div className={'card-configure__icon'}>
											<WarningIndicator />
										</div>
									</div>
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Exit Share Data
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className='share__card-configure-text'>Are you sure you want to exit Sharing Data? Your progress will be lost.</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button ghost text="Cancel"
								data-testid="exit-share-data-cancel-button"
								submit={() => {
									hideCancelModal()
								}} />
							<span style={{ width: rem(10), display: 'inline-block' }}></span>
							<Button text="Confirm"
								warning
								data-testid="exit-share-data-confirm-button"
								id="submit-button-1"
								submit={() => {
									hideCancelModal();
									handleReset();
									goTo(`/shared-data`);
								}}
							/>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Modal visible={previousModal} variant="medium" close={hidePreviousModal}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__left">
										<div className={'card-configure__icon'}>
											<WarningIndicator />
										</div>
									</div>
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Return to Previous Step
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className='share__card-configure-text'>Are you sure to go back to previous page? Your progress will be lost.</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button ghost text="Cancel"
								data-testid="previous-step-modal-cancel-button"
								submit={() => {
									hidePreviousModal()
								}} />
							<span style={{ width: rem(10), display: 'inline-block' }}></span>
							<Button text="Confirm"
								data-testid="previous-step-modal-confirm-button"
								warning
								submit={() => {
									hidePreviousModal()
									setError('')
									setErrorList([])
									resetState()
								}}
							/>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Modal visible={mismatchModal} variant="medium" close={hideMismatchModal}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__left">
										<div className={'card-configure__icon'}>
											<WarningIndicator />
										</div>
									</div>
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Mismatched Attachment Name
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className='share__card-configure-text'>Uploaded attachments does not match any of the required attachment name.</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button ghost text="Close"
								data-testid="mismatched-attachment-close-button"
								submit={() => {
									hideMismatchModal()
									clearAttachment()
								}} />
							<span style={{ width: rem(10), display: 'inline-block' }}></span>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Modal visible={duplicateModal} variant="medium" close={hideDuplicateModal}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
						<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__left">
										<div className={'card-configure__icon'}>
											<WarningIndicator />
										</div>
									</div>
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
										Duplicate File
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className='share__card-configure-text'>You have uploaded the same file.</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button ghost text="Close"
								data-testid="duplicate-close-button"
								submit={() => {
									hideDuplicateModal()
								}} />
							<span style={{ width: rem(10), display: 'inline-block' }}></span>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>
		</>
	)
}

export default SharePage
